import chalk from 'chalk';
import {execa} from 'execa';
// @ts-ignore
import fetchGitRepo from 'fetch-git-repo-async';
import fse from 'fs-extra';
import ora, {Ora} from 'ora';
import pLimit from 'p-limit';
import path from 'path';

import {cloneKogFiles, clonePackageTemplate} from '@/utilities/clone';
import {getConfigDir, getConfigOrExit} from '@/utilities/config';
import {fetchJsonFromGitRepository, fetchLocalJson, fetchLocalPackage} from '@/utilities/fetch';
import {directoryExists} from '@/utilities/files';
import {logger} from '@/utilities/logger';
import {getPackageManager} from '@/utilities/packageManager';

const directoryKog = `${getConfigDir()}/kog`;

export const kogIsInstalled = async (currentWorkingDirectory: string = directoryKog) => fse.existsSync(currentWorkingDirectory);

export const kogIsLatestVersion = async (currentWorkingDirectory: string = directoryKog) => {
  // Read version of Kog from local copy.
  const packageKogLocal = await fetchLocalPackage(currentWorkingDirectory);
  const {name: nameLocal, version: versionLocal} = packageKogLocal;

  // Read version of Kog from production.
  const packageKogProduction = await fetchJsonFromGitRepository(process.env.GITLAB_URL_KOG_PACKAGE ?? '');
  const {name: nameProduction, version: versionProduction} = packageKogProduction;

  return nameLocal !== nameProduction && versionLocal !== versionProduction;
};

export const cloneKogTemplate = async (currentWorkingDirectory: string = directoryKog) => {
  const config = await getConfigOrExit();
  const currentWorkingDirectoryPath = path.resolve(currentWorkingDirectory);

  fse.remove(currentWorkingDirectoryPath);
  return fetchGitRepo(`direct:${process.env.GITLAB_URL_KOG_ZIP}`, currentWorkingDirectoryPath, {
    headers: {'PRIVATE-TOKEN': config.gitlabToken},
  });
};

export const updateKogTemplate = async (spinner: Ora | undefined = undefined) => {
  if ((await kogIsInstalled()) === false) {
    // Store a copy of Kog locally.
    /* eslint-disable-next-line no-param-reassign */
    if (spinner) spinner.text = 'Installing KOG...';
    await cloneKogTemplate();
  } else if ((await kogIsLatestVersion()) === false) {
    // If Kog is not up to date, update the local copy.
    /* eslint-disable-next-line no-param-reassign */
    if (spinner) spinner.text = 'Updating KOG...';
    await cloneKogTemplate();
  }
};

export const loadKogRegistry = async (currentWorkingDirectory: string = directoryKog): Promise<{kogRegistry: KogRegistry; kogRegistryKeys: string[]}> => {
  const kogRegistry = (await fetchLocalJson(`${currentWorkingDirectory}/kog.registry.json`)) as unknown as KogRegistry;
  const kogRegistryKeys = Object.keys(kogRegistry);

  return {kogRegistry, kogRegistryKeys};
};

export const cloneKogRegistryItem = async (
  registryItemName: string,
  kogRegistry: KogRegistry,
  currentWorkingDirectory: string = process.cwd(),
  optionOverride: boolean = false,
  displaySpinner: boolean = false
) => {
  const limit = pLimit(1);
  const spinner = ora();

  if (displaySpinner) spinner.start(`Installing ${registryItemName}`);

  const registryItem = kogRegistry[registryItemName];
  if (!registryItem) {
    if (displaySpinner) spinner.fail(`Installing ${registryItemName} - Skipped. It does not exist.`);
    return;
  }

  // Install registry items.
  if (registryItem.files?.length) {
    const files = registryItem.files.map(file =>
      limit(async () => {
        if (displaySpinner) spinner.text = `Installing "${file}" (file)`;

        // Check if the registry item already exists, warn the user to use override.
        if (!optionOverride && (await directoryExists(`${currentWorkingDirectory}/src/${file}`))) {
          logger.warn(`${displaySpinner ? ` - Skipped` : `Skipped "${file}" (file)`}. To override, run with the ${chalk.green('--override')} flag.`);
        } else {
          // Clone the file or directory and save to the target directory.
          return cloneKogFiles(file, currentWorkingDirectory);
        }

        return Promise.resolve();
      })
    );

    await Promise.all(files);
  }

  // Install package dependencies.
  if (registryItem.packageDependencies?.length) {
    if (displaySpinner) spinner.text = `Installing "${registryItemName}" (package dependencies)`;
    const packageManager = await getPackageManager(currentWorkingDirectory);

    // If the package.json file does not exist, create it.
    if ((await directoryExists(`${currentWorkingDirectory}/package.json`)) === false) {
      await clonePackageTemplate(currentWorkingDirectory);
    }

    await execa(packageManager, [packageManager === 'npm' ? 'install' : 'add', ...registryItem.packageDependencies], {
      cwd: currentWorkingDirectory,
    });
  }

  if (registryItem.registryDependencies?.length) {
    // To confirm it's completely installed, use recursion to install the dependency's dependencies
    const registryDependencies = registryItem.registryDependencies.map(file =>
      limit(() => cloneKogRegistryItem(file, kogRegistry, currentWorkingDirectory, optionOverride, displaySpinner))
    );

    await Promise.all(registryDependencies);
  }

  if (displaySpinner) spinner.succeed(`Installed ${registryItemName}`);
};
